/**
 * `createApiTracersSource` — the `pipeline-mgmt-api` tracers port over the shared
 * `ApiClient`.
 *
 * Tracers are "follow the money" flow-tracking definitions; each run snapshots
 * the discovered `InteractionGraph` (which `@authe/tx-graph` renders). The
 * backend speaks snake_case (`max_hops`, `min_value_raw`, seeds as
 * `{chain_id, value}`), so this adapter maps both directions and exposes a
 * camelCase domain to the feature page. Canonical `/tracers/*` paths are mapped
 * to `private/pipeline-mgmt-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  asNullableString,
  asNumber,
  asOptString,
  asRecord,
  asString,
  asStringList,
  rowsAt,
} from "./wire";
import { walkPages } from "./pagedList";

export type FlowDirection = "outbound" | "inbound" | "both";
export type TracerStatus = "active" | "paused" | "archived";
/**
 * The tenancy rung a tracer is authored at, and therefore who can read it.
 *
 * `commons.tenancy.ladder`'s vocabulary — the same words a transaction widen and
 * a warehouse read use. The rung decides the row's PARTITION: a `tenant` tracer
 * is visible to the whole tenant, an `org` one to every tenant of that org, and
 * so on up to `system` (the platform's own, readable fleet-wide).
 *
 * There is no `team` rung. Sharing one person's tracer with their team is
 * {@link TracerSummary.visibleToTeamIds}, an ABAC carrier on the row — a
 * different mechanism, and carrying both is what made the enum look like a
 * working ladder while only the carriers were read.
 */
export type TracerScope = "user" | "tenant" | "org" | "customer" | "system";

/** Rungs the dashboard offers when authoring. `system` is internal-only. */
export const AUTHORABLE_SCOPES: readonly TracerScope[] = [
  "user",
  "tenant",
  "org",
  "customer",
] as const;

export interface TracerSeed {
  chainId: string;
  value: string;
}

export interface TracerSummary {
  id: string;
  name: string;
  description?: string | null;
  scope: TracerScope;
  status: TracerStatus;
  /**
   * The case this tracer was started from, when it was started from one.
   *
   * The hand-off used to record the case in prose inside the description, which
   * nothing could read back — so a tracer could not link to its case and a case
   * could not tell it had already been traced.
   */
  investigationId?: string | null;
  direction: FlowDirection;
  maxHops: number;
  seeds: TracerSeed[];
  chainIds: string[];
  tokenFilters: string[];
  minValueRaw?: string | null;
  followBridges: boolean;
  lastRunId?: string | null;
  lastRunAt?: number | null;
  runCount: number;
  /** Teams this tracer is shared with (backend ABAC `visible_to_team_ids`). */
  visibleToTeamIds: string[];
  /**
   * Whether THIS caller may change this tracer.
   *
   * Answered by the server, because only it knows the caller's tenancy ladder.
   * The page used to gate Edit / Delete / Run on holding `tracers:write` at all
   * — a permission, not a row — so a tenant was shown a full set of controls on
   * a platform tracer and got a 403 from every one of them.
   */
  canEdit: boolean;
}

/** A node/edge graph snapshot shaped like the backend `InteractionGraph`. */
export interface TracerRunGraph {
  nodes: Record<string, unknown>[];
  edges: Record<string, unknown>[];
  node_count: number;
  edge_count: number;
  chain_ids: string[];
  multi_chain: boolean;
}

/**
 * How much of an answer a run's graph is.
 *
 * `ok` is the ONLY one from which "no edges" may be read as a statement about
 * the chain — that value did not move. `unavailable` means no flow data source
 * was wired, so nothing was traced; the page must not render it as an empty
 * result. Kept in step with `commons.interfaces.models.tracer_run`.
 */
export const TRACER_RUN_STATUSES = [
  "running",
  "ok",
  "partial",
  "unavailable",
  "failed",
] as const;
export type TracerRunStatus = (typeof TRACER_RUN_STATUSES)[number];

export interface TracerRunSummary {
  id: string;
  status: TracerRunStatus;
  /** Why the graph is not a complete answer. `null` when it is one. */
  statusReason: string | null;
  /**
   * Which walk bounds stopped this run (`hops`, `nodes`, `share`, ...).
   *
   * `statusReason` says the same thing in a sentence; this is the half a
   * renderer can act on — a node with no onward edge in a bounded graph is a
   * place WE stopped, not a place the money did.
   */
  boundedBy: string[];
  hopsWalked: number;
  nodeCount: number;
  edgeCount: number;
  totalValueRaw: string;
  terminalNodeCount: number;
  /** Chains the discovered graph touches. Empty until the run finds something. */
  chainIds: string[];
  multiChain: boolean;
  startedAt?: number | null;
  finishedAt?: number | null;
  durationMs?: number | null;
  error?: string | null;
  createdAt?: number | null;
  /**
   * The node/edge graph, present only on a run fetched by id.
   *
   * `null` on a row from `listRuns`, which sends rollups only: the list used to
   * carry every run's complete graph — each one rehydrated from S3 — for a page
   * that renders exactly one at a time.
   */
  graph: TracerRunGraph | null;
}

export interface CreateTracerInput {
  name: string;
  description?: string | null;
  direction: FlowDirection;
  maxHops: number;
  seeds: TracerSeed[];
  chainIds?: string[];
  tokenFilters?: string[];
  minValueRaw?: string | null;
  followBridges?: boolean;
  scope?: TracerScope;
  /** The case this tracer was started from. See {@link TracerSummary.investigationId}. */
  investigationId?: string | null;
  /** Teams to share the tracer with (backend ABAC `visible_to_team_ids`). */
  visibleToTeamIds?: string[];
}

/** One address cluster the engine found in a run's graph. */
export interface TracerCluster {
  clusterId: string;
  members: string[];
  confidence: number;
  evidence: Record<string, unknown>[];
}

/** The cluster overlay on a tracer's latest run. */
export interface TracerClusters {
  runId: string;
  runStatus: TracerRunStatus;
  seed: string;
  reached: string[];
  clusters: TracerCluster[];
  seedCluster: TracerCluster | null;
}

export interface TracersSource {
  list(): Promise<TracerSummary[]>;
  get(id: string): Promise<TracerSummary>;
  /** Run rollups, newest first. No graphs — use {@link getRun} for one. */
  /**
   * A tracer's runs.
   *
   * `fresh` bypasses the client's 30-second GET cache for this read. A poll
   * waiting for a run to land must pass it: `runNow` invalidates the cache, the
   * first poll refills it with the still-empty list, and every poll inside the
   * next thirty seconds is then served that copy — so a walk that finished in
   * 243ms stayed invisible on its own page until something else read past the
   * TTL. A loop that reads through a cache it refills is polling itself.
   */
  listRuns(id: string, opts?: { fresh?: boolean }): Promise<TracerRunSummary[]>;
  /** One run WITH its node/edge graph. */
  getRun(id: string, runId: string): Promise<TracerRunSummary>;
  /** The cluster overlay on the tracer's latest run. */
  clusters(id: string): Promise<TracerClusters>;
  create(input: CreateTracerInput): Promise<TracerSummary>;
  update(id: string, patch: Partial<CreateTracerInput> & { status?: TracerStatus }): Promise<TracerSummary>;
  remove(id: string): Promise<void>;
  runNow(id: string): Promise<{ queued: boolean; tracerId: string }>;
}

function seedFromWire(raw: Record<string, unknown>): TracerSeed {
  return { chainId: asString(raw.chain_id ?? raw.chainId), value: asString(raw.value) };
}

function tracerFromWire(raw: Record<string, unknown>): TracerSummary {
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    description: asNullableString(raw.description),
    scope: asString(raw.scope, "user") as TracerScope,
    status: asString(raw.status, "active") as TracerStatus,
    investigationId: asNullableString(raw.investigation_id ?? raw.investigationId),
    direction: asString(raw.direction, "outbound") as FlowDirection,
    maxHops: Number(raw.max_hops ?? raw.maxHops ?? 3),
    seeds: rowsAt(raw, "seeds").map(seedFromWire),
    chainIds: asStringList(raw.chain_ids ?? raw.chainIds),
    tokenFilters: asStringList(raw.token_filters ?? raw.tokenFilters),
    minValueRaw: asNullableString(raw.min_value_raw ?? raw.minValueRaw),
    followBridges: Boolean(raw.follow_bridges ?? raw.followBridges ?? true),
    lastRunId: asNullableString(raw.last_run_id ?? raw.lastRunId),
    lastRunAt: asNumber(raw.last_run_at ?? raw.lastRunAt),
    runCount: Number(raw.run_count ?? raw.runCount ?? 0),
    visibleToTeamIds: asStringList(raw.visible_to_team_ids ?? raw.visibleToTeamIds),
    // Absent (an older build, a row from a route that does not compute it) reads
    // as NOT editable: offering a control that 403s is worse than withholding
    // one that would have worked.
    canEdit: raw.can_edit === true,
  };
}

function tracerToWire(input: Partial<CreateTracerInput> & { status?: TracerStatus }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.direction !== undefined) body.direction = input.direction;
  if (input.maxHops !== undefined) body.max_hops = input.maxHops;
  if (input.seeds !== undefined) {
    body.seeds = input.seeds.map((s) => ({ chain_id: s.chainId, value: s.value }));
  }
  if (input.chainIds !== undefined) body.chain_ids = input.chainIds;
  if (input.tokenFilters !== undefined) body.token_filters = input.tokenFilters;
  if (input.minValueRaw !== undefined) body.min_value_raw = input.minValueRaw;
  if (input.followBridges !== undefined) body.follow_bridges = input.followBridges;
  if (input.scope !== undefined) body.scope = input.scope;
  if (input.investigationId !== undefined) body.investigation_id = input.investigationId;
  if (input.status !== undefined) body.status = input.status;
  if (input.visibleToTeamIds !== undefined) body.visible_to_team_ids = input.visibleToTeamIds;
  return body;
}

function runFromWire(raw: Record<string, unknown>): TracerRunSummary {
  // `graph_included: false` marks a rollup row from the list route. A row
  // without it is a run fetched by id, which carries its graph.
  const hasGraph = raw.graph_included !== false;
  const nodes = hasGraph ? rowsAt(raw, "nodes") : [];
  const edges = hasGraph ? rowsAt(raw, "edges") : [];
  const chainIds = asStringList(raw.chain_ids);
  return {
    id: asString(raw.id),
    // NOT `?? "ok"`. A status this build does not recognise used to land on
    // the one value that licenses reading an empty graph as "the money did not
    // move" — so a backend that grows a status makes this page state, with
    // confidence, the most consequential thing it can say. `running` is the
    // non-committal fallback: it renders no claim about the chain at all.
    status: (TRACER_RUN_STATUSES as readonly string[]).includes(asString(raw.status))
      ? (asString(raw.status) as TracerRunStatus)
      : "running",
    statusReason: asNullableString(raw.status_reason),
    boundedBy: asStringList(raw.bounded_by),
    hopsWalked: Number(raw.hops_walked ?? 0),
    // The rollup, never the array length: on a list row the arrays are absent
    // by design, and falling back to `0` would render "0 nodes" for a run that
    // found plenty.
    nodeCount: Number(raw.node_count ?? nodes.length),
    edgeCount: Number(raw.edge_count ?? edges.length),
    totalValueRaw: asString(raw.total_value_raw, "0"),
    terminalNodeCount: Number(raw.terminal_node_count ?? 0),
    chainIds,
    multiChain: Boolean(raw.multi_chain ?? chainIds.length > 1),
    startedAt: asNumber(raw.started_at),
    finishedAt: asNumber(raw.finished_at),
    durationMs: asNumber(raw.duration_ms),
    error: asNullableString(raw.error),
    createdAt: asNumber(raw.created_at),
    graph: hasGraph
      ? {
          nodes,
          edges,
          node_count: Number(raw.node_count ?? nodes.length),
          edge_count: Number(raw.edge_count ?? edges.length),
          chain_ids: chainIds,
          multi_chain: Boolean(raw.multi_chain ?? chainIds.length > 1),
        }
      : null,
  };
}

function clusterFromWire(raw: Record<string, unknown>): TracerCluster {
  return {
    clusterId: asString(raw.cluster_id ?? raw.clusterId),
    members: asStringList(raw.members),
    confidence: Number(raw.confidence ?? 0),
    evidence: rowsAt(raw, "evidence"),
  };
}

export function createApiTracersSource(client: ApiClient): TracersSource {
  const tracerPath = (id: string) => `/tracers/${encodeURIComponent(id)}`;

  /**
   * Drop the tracer reads after every write.
   *
   * There was NO invalidation here at all: create, update, remove and `runNow`
   * all returned a fresh row and left the 30s GET cache holding the pre-write
   * one, so the page redrew from it and the change read as silently dropped.
   *
   * `invalidateMatching`, because `list` / `listRuns` read through `walkPages`
   * and so always carry `?limit=100`: the exact-URL `invalidate` keys on the
   * resolved URL query string included, and would match none of them. One
   * fragment covers the list, `/tracers/{id}` and its runs/clusters children.
   */
  const invalidate = (): void => client.invalidateMatching("/tracers");

  return {
    async list(): Promise<TracerSummary[]> {
      // Paged. `/tracers` layers the system defaults in front of the caller's
      // own and filters for visibility after taking the page, so this saw the
      // first fifty rows minus whatever it could not see — on a management
      // surface, where a missing tracer reads as one that does not exist.
      return (await walkPages(client, "/tracers")).map(tracerFromWire);
    },
    async get(id: string): Promise<TracerSummary> {
      return tracerFromWire(asRecord(unwrapData(await client.get<unknown>(tracerPath(id)))));
    },
    async listRuns(id: string, opts?: { fresh?: boolean }): Promise<TracerRunSummary[]> {
      // Paged, and here the truncation was VISIBLE and contradictory: the
      // sidebar renders the tracer's server-maintained `runCount` right beside
      // this list, so a tracer with eighty runs said "80 runs" above a list of
      // fifty that simply stopped. Rollups only — walking every page of full
      // graphs is what made paging expensive in the first place.
      return (
        await walkPages(client, `${tracerPath(id)}/runs`, opts?.fresh ? { cacheTtlMs: 0 } : undefined)
      ).map(runFromWire);
    },
    async getRun(id: string, runId: string): Promise<TracerRunSummary> {
      const path = `${tracerPath(id)}/runs/${encodeURIComponent(runId)}`;
      return runFromWire(asRecord(unwrapData(await client.get<unknown>(path))));
    },
    async clusters(id: string): Promise<TracerClusters> {
      const raw = asRecord(unwrapData(await client.get<unknown>(`${tracerPath(id)}/clusters`)));
      const trace = asRecord(raw.trace ?? raw);
      const seedCluster = raw.seed_cluster;
      return {
        runId: asOptString(raw.run_id) ?? "",
        runStatus: asString(raw.run_status, "running") as TracerRunStatus,
        seed: asString(trace.seed),
        reached: asStringList(trace.reached),
        clusters: rowsAt(raw, "clusters").map(clusterFromWire),
        seedCluster: seedCluster ? clusterFromWire(asRecord(seedCluster)) : null,
      };
    },
    async create(input: CreateTracerInput): Promise<TracerSummary> {
      const saved = tracerFromWire(
        asRecord(unwrapData(await client.post<unknown>("/tracers", tracerToWire(input)))),
      );
      invalidate();
      return saved;
    },
    async update(id, patch): Promise<TracerSummary> {
      const saved = tracerFromWire(
        asRecord(unwrapData(await client.patch<unknown>(tracerPath(id), tracerToWire(patch)))),
      );
      invalidate();
      return saved;
    },
    async remove(id: string): Promise<void> {
      unwrapData(await client.delete<void>(tracerPath(id)));
      invalidate();
    },
    async runNow(id: string): Promise<{ queued: boolean; tracerId: string }> {
      const raw = asRecord(unwrapData(await client.post<unknown>(`${tracerPath(id)}/run`, {})));
      // A queued run changes the tracer's run list and its last-run summary.
      invalidate();
      return { queued: Boolean(raw.queued), tracerId: asOptString(raw.tracer_id) ?? id };
    },
  };
}
